import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, Switch, Button, Row, Col, Upload, message, Card, Space } from 'antd';
import { UploadOutlined, SettingOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons';
import { useCategories } from '../hooks/useCategories';
import { useProductAnalyzer } from '../hooks/useProductAnalyzer';
import CategoriesManager from './CategoriesManager';
import { useStateContext } from "../../../../../contexts/ContextProvider";

const { TextArea } = Input;
const { Option } = Select;

function ProdusForm({ initialValues, onSubmit, onCancel }) {
  const [form] = Form.useForm();
  const [isCategoriesModalVisible, setIsCategoriesModalVisible] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [isManualPrice, setIsManualPrice] = useState(false);
  const [selectedFurnizor, setSelectedFurnizor] = useState(null);
  const { getFlatCategories, loading: categoriesLoading, refreshCategories } = useCategories();
  const { analyzeProductFromUrl, analyzing, progress, cleanup } = useProductAnalyzer();
  const { token } = useStateContext();

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setImagePreview(initialValues.poza_principala_url || '');
      
      if (initialValues.furnizori && initialValues.furnizori.length > 0) {
        const sortedFurnizori = [...initialValues.furnizori].sort((a, b) => a.pret - b.pret);
        const defaultFurnizor = sortedFurnizori[0];
        setSelectedFurnizor(defaultFurnizor);
        
        form.setFieldsValue({
          furnizor_denumire: defaultFurnizor.nume,
          pret_valoare: defaultFurnizor.pret,
          pret_valuta: defaultFurnizor.valuta || 'MDL',
          product_url: defaultFurnizor.product_url || ''
        });
        
        // Logica pentru prețul de vânzare
        if (initialValues.pret_vanzare && initialValues.pret_vanzare > 0) {
          // Dacă produsul are deja un preț de vânzare, îl folosim și setăm modul manual
          form.setFieldsValue({ pret_vanzare: initialValues.pret_vanzare });
          setIsManualPrice(true);
        } else {
          // Dacă nu are preț de vânzare, calculăm automat +30% din prețul furnizorului
          const vanzarePrice = calculateVanzarePrice(defaultFurnizor.pret);
          form.setFieldsValue({ pret_vanzare: vanzarePrice });
          setIsManualPrice(false);
        }
      } else {
        // Pentru produse fără furnizori, verificăm dacă au preț de vânzare
        if (initialValues.pret_vanzare && initialValues.pret_vanzare > 0) {
          form.setFieldsValue({ pret_vanzare: initialValues.pret_vanzare });
          setIsManualPrice(true);
        } else if (initialValues.pret_valoare) {
          // Calculăm din prețul de achiziție existent
          const vanzarePrice = calculateVanzarePrice(initialValues.pret_valoare);
          form.setFieldsValue({ pret_vanzare: vanzarePrice });
          setIsManualPrice(false);
        }
      }
    } else {
      form.resetFields();
      setImagePreview('');
      setSelectedFurnizor(null);
      setIsManualPrice(false);
    }
  }, [initialValues, form]);


  // Cleanup la unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const handleSubmit = async (values) => {
    try {
      // Actualizare optimistă
      if (initialValues) {
        // Pentru editare, actualizăm direct în cache
        const updatedProdus = { ...initialValues, ...values };
        await onSubmit(updatedProdus);
      } else {
        // Pentru creare, trimitem datele și așteptăm răspunsul
        await onSubmit(values);
      }
    } catch (error) {
      message.error('A apărut o eroare la salvarea produsului');
      console.error('Eroare la salvarea produsului:', error);
    }
  };

  // Funcție helper pentru a gestiona URL-urile imaginilor
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  const uploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/upload?token=${token}`,
    headers: {
      authorization: 'authorization-text',
    },
    onChange(info) {
      if (info.file.status === 'done') {
        message.success(`${info.file.name} a fost încărcat cu succes`);
        const imageUrl = info.file.response.url;
        form.setFieldsValue({ poza_principala_url: imageUrl });
        setImagePreview(imageUrl);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} nu a putut fi încărcat.`);
      }
    },
  };

  const buildCategoryTree = (categories) => {
    if (!categories || categories.length === 0) return [];
    
    const categoryMap = {};
    const rootCategories = [];

    // Creăm un map pentru acces rapid
    categories.forEach(cat => {
      categoryMap[cat.id] = { ...cat, children: [] };
    });

    // Construim arborele
    categories.forEach(cat => {
      if (cat.parent_id === null || cat.parent_id === undefined) {
        rootCategories.push(categoryMap[cat.id]);
      } else {
        if (categoryMap[cat.parent_id]) {
          categoryMap[cat.parent_id].children.push(categoryMap[cat.id]);
        }
      }
    });

    return rootCategories;
  };

  const renderCategoryOptions = (categories, level = 0, parentName = '') => {
    if (!categories || categories.length === 0) return [];
    
    const options = [];
    
    categories.forEach(category => {
      let displayName;
      if (level === 0) {
        displayName = category.category_name;
      } else {
        displayName = `${parentName} > ${category.category_name}`;
      }
      
      options.push(
        <Option key={category.id} value={category.id}>
          {displayName}
        </Option>
      );

      if (category.children && category.children.length > 0) {
        options.push(...renderCategoryOptions(category.children, level + 1, displayName));
      }
    });

    return options;
  };

  const flatCategories = getFlatCategories();
  const categoryTree = buildCategoryTree(flatCategories);

  console.log('Flat categories:', flatCategories);
  console.log('Category tree:', categoryTree);

  const handleCategoriesModalClose = () => {
    setIsCategoriesModalVisible(false);
    // Reîmprospătăm categoriile când se închide modalul
    refreshCategories();
  };

  const handleAnalyzeUrl = async () => {
    if (!productUrl.trim()) {
      message.warning('Vă rugăm să introduceți un URL valid');
      return;
    }

    const response = await analyzeProductFromUrl(productUrl);
    if (response && response.data) {
      const analyzedData = response.data;
      
      // Precompletăm formularul cu datele analizate
      const formData = {
        denumire: analyzedData.denumire || '',
        descriere_scurta: analyzedData.descriere_scurta || '',
        descriere_lunga: analyzedData.descriere_lunga || '',
        poza_principala_url: analyzedData.poza_principala_url || '',
        barcode: analyzedData.barcode || '',
        sku_intern: analyzedData.sku_intern || '',
        category_id: analyzedData.category_id || undefined,
        lungime: analyzedData.dimensiuni?.lungime || null,
        latime: analyzedData.dimensiuni?.latime || null,
        inaltime: analyzedData.dimensiuni?.inaltime || null,
        greutate: analyzedData.greutate || null,
        unit_of_measure: analyzedData.unit_of_measure === 'bucată' ? 'buc' : analyzedData.unit_of_measure || undefined,
        // Adăugăm datele furnizorului
        furnizor_mongo_id: analyzedData.furnizor?.mongo_id_furnizor || null,
        furnizor_denumire: analyzedData.furnizor?.denumire_furnizor || '',
        furnizor_brand: analyzedData.furnizor?.brand_furnizor || '',
        furnizor_website: analyzedData.furnizor?.website_furnizor || '',
        pret_valoare: analyzedData.furnizor?.pret?.valoare || null,
        pret_valuta: analyzedData.furnizor?.pret?.valuta || 'MDL',
        product_url: productUrl, // Setăm URL-ul analizat
      };
      
      // Actualizăm și preview-ul imaginii
      setImagePreview(analyzedData.poza_principala_url || '');
      
      // Combinăm valorile existente cu datele analizate
      const currentValues = form.getFieldsValue();
      const finalFormData = {
        ...currentValues,
        ...formData
      };
      
      form.setFieldsValue(finalFormData);

      // Calculăm prețul de vânzare după analiza URL-ului
      if (!isManualPrice && formData.pret_valoare) {
        const vanzarePrice = calculateVanzarePrice(formData.pret_valoare);
        form.setFieldsValue({ pret_vanzare: vanzarePrice });
      }

      setProductUrl(''); // Resetăm URL-ul după analiză
      message.success('Formularul a fost precompletat cu datele analizate');
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImagePreview(url);
    // Nu mai e nevoie de setFieldsValue aici, se face automat prin Form.Item
  };

  const calculateVanzarePrice = (achizitiePrice) => {
    if (!achizitiePrice) return null;
    return Number((achizitiePrice * 1.3).toFixed(2));
  };

  const handlePretAchizitieChange = (value) => {
    if (!isManualPrice) {
      const vanzarePrice = calculateVanzarePrice(value);
      form.setFieldsValue({ pret_vanzare: vanzarePrice });
    }
  };

  // Funcție pentru selectarea furnizorului
  const handleFurnizorChange = (furnizorId) => {
    if (!initialValues?.furnizori) return;
    
    const furnizor = initialValues.furnizori.find(f => f.id === furnizorId);
    if (furnizor) {
      setSelectedFurnizor(furnizor);
      
      // Actualizăm formularul cu datele furnizorului selectat
      form.setFieldsValue({
        furnizor_denumire: furnizor.nume,
        pret_valoare: furnizor.pret,
        pret_valuta: furnizor.valuta || 'MDL',
        product_url: furnizor.product_url || ''
      });
      
      // Recalculăm prețul de vânzare doar dacă nu e setat manual
      // și doar dacă produsul nu avea deja un preț de vânzare fix
      if (!isManualPrice) {
        const vanzarePrice = calculateVanzarePrice(furnizor.pret);
        form.setFieldsValue({ pret_vanzare: vanzarePrice });
      }
    }
  };

  return (
    <>
      {/* Card pentru analiza URL-ului */}
      <Card 
        title="Analizează produs din URL" 
        className="mb-4"
        size="small"
      >
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Introduceți URL-ul produsului pentru analiză automată..."
            value={productUrl}
            onChange={(e) => setProductUrl(e.target.value)}
            onPressEnter={handleAnalyzeUrl}
            disabled={analyzing}
          />
          <Button 
            type="primary" 
            icon={analyzing ? <LoadingOutlined /> : <LinkOutlined />}
            onClick={handleAnalyzeUrl}
            loading={analyzing}
            disabled={!productUrl.trim() || analyzing}
          >
            {analyzing ? 'Analizez...' : 'Analizează'}
          </Button>
        </Space.Compact>
        {analyzing && progress && (
          <div className="text-sm text-blue-600 mt-2 flex items-center">
            <LoadingOutlined className="mr-2" />
            {progress}
          </div>
        )}
        {!analyzing && (
          <div className="text-xs text-gray-500 mt-2">
            Introduceți URL-ul unui produs pentru a precompletat automat formularul cu informațiile disponibile.
          </div>
        )}
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-4"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="denumire"
              label="Denumire Produs"
              rules={[{ required: true, message: 'Denumirea este obligatorie' }]}
            >
              <Input placeholder="Introduceți denumirea produsului" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="sku_intern"
              label="SKU Intern (generat automat)"
              
            >
              <Input disabled={true} placeholder="SKU intern" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={20}>
            <Form.Item
              name="category_id"
              label="Categorie"
            >
              <Select
                placeholder="Selectează categoria"
                loading={categoriesLoading}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {renderCategoryOptions(categoryTree)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label=" ">
              <Button
                icon={<SettingOutlined />}
                onClick={() => setIsCategoriesModalVisible(true)}
                title="Gestionează categoriile"
              >
                Categorii
              </Button>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="unit_of_measure"
              label="Unitate de măsură"
            >
              <Select placeholder="Selectează unitatea">
                <Option value="buc">Bucată</Option>
                <Option value="kg">Kilogram</Option>
                <Option value="m">Metru</Option>
                <Option value="m2">Metru pătrat</Option>
                <Option value="m3">Metru cub</Option>
                <Option value="l">Litru</Option>
                <Option value="ml">Mililitru</Option>
                <Option value="set">Set</Option>
                <Option value="pereche">Pereche</Option>
                <Option value="pachet">Pachet</Option>
                <Option value="cutie">Cutie</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="barcode"
              label="Cod de bare"
            >
              <Input placeholder="Cod de bare" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="descriere_scurta"
          label="Descriere scurtă"
        >
          <TextArea rows={2} placeholder="Descriere scurtă a produsului" />
        </Form.Item>

        <Form.Item
          name="descriere_lunga"
          label="Descriere detaliată"
        >
          <TextArea rows={4} placeholder="Descriere detaliată a produsului" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={imagePreview ? 16 : 24}>
            <Form.Item
              name="poza_principala_url"
              label="Imagine principală"
            >
              <Input 
                placeholder="URL imagine sau încarcă mai jos" 
                onChange={handleImageUrlChange}
              />
            </Form.Item>
          </Col>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.poza_principala_url !== currentValues.poza_principala_url
            }
          >
            {({ getFieldValue }) => {
              const imageUrl = getFieldValue('poza_principala_url');
              if (imageUrl && imageUrl !== imagePreview) {
                setImagePreview(imageUrl);
              }
              
              return imagePreview ? (
                <Col span={8}>
                  <Form.Item label="Preview">
                    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                      <img
                        src={getImageUrl(imagePreview)}
                        alt="Preview imagine produs"
                        className="w-full h-32 object-contain rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div 
                        className="text-center text-gray-500 text-sm py-8"
                        style={{ display: 'none' }}
                      >
                        Imagine invalidă
                      </div>
                    </div>
                  </Form.Item>
                </Col>
              ) : null;
            }}
          </Form.Item>
        </Row>

        <Form.Item label="Încarcă imagine">
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Selectează fișier</Button>
          </Upload>
        </Form.Item>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="lungime"
              label="Lungime (cm)"
            >
              <InputNumber
                min={0}
                step={0.1}
                placeholder="0.0"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="latime"
              label="Lățime (cm)"
            >
              <InputNumber
                min={0}
                step={0.1}
                placeholder="0.0"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="inaltime"
              label="Înălțime (cm)"
            >
              <InputNumber
                min={0}
                step={0.1}
                placeholder="0.0"
                className="w-full"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="greutate"
              label="Greutate (kg)"
            >
              <InputNumber
                min={0}
                step={0.01}
                placeholder="0.00"
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="is_active"
          label="Status"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch checkedChildren="Activ" unCheckedChildren="Inactiv" />
        </Form.Item>

        {/* Secțiunea Furnizor și Preț */}
        <Card title="Furnizor și Preț" size="small" className="mb-4">
          <Form.Item
            name="furnizor_mongo_id"
            hidden
          >
            <Input />
          </Form.Item>

          {/* Selector pentru furnizori existenți (doar la editare) */}
          {initialValues && initialValues.furnizori && initialValues.furnizori.length > 0 && (
            <Form.Item
              label="Selectează furnizorul pentru preîncărcarea datelor"
            >
              <Select
                placeholder="Alege furnizorul..."
                value={selectedFurnizor?.id}
                onChange={handleFurnizorChange}
                className="w-full"
              >
                {initialValues.furnizori
                  .sort((a, b) => a.pret - b.pret)
                  .map((furnizor, index) => (
                    <Option key={furnizor.id} value={furnizor.id}>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {furnizor.nume}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            {furnizor.pret} {furnizor.valuta || 'MDL'}
                          </span>
                          {index === 0 && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Cel mai bun preț
                            </span>
                          )}
                        </div>
                      </div>
                    </Option>
                  ))}
              </Select>
              <div className="text-xs text-gray-500 mt-1">
                Selectând un furnizor se vor preîncărca automat datele acestuia în formularul de mai jos
              </div>
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="furnizor_denumire"
                label="Denumire Furnizor"
              >
                <Input placeholder="Introduceți denumirea furnizorului" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="furnizor_brand"
                label="Brand Furnizor"
              >
                <Input placeholder="Brand/Marca furnizorului" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="furnizor_website"
                label="Website Furnizor"
              >
                <Input placeholder="Website-ul furnizorului (ex: www.example.com)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="product_url"
                label="URL Produs la Furnizor"
              >
                <Input placeholder="Link-ul direct către produs la furnizor" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pret_valoare"
                label="Preț de achiziție"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  className="w-full"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  onChange={handlePretAchizitieChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pret_valuta"
                label="Valută"
                initialValue="MDL"
              >
                <Select placeholder="Selectează valuta">
                  <Option value="MDL">MDL (Leu moldovenesc)</Option>
                  <Option value="RON">RON (Leu românesc)</Option>
                  <Option value="EUR">EUR (Euro)</Option>
                  <Option value="USD">USD (Dolar american)</Option>
                  <Option value="UAH">UAH (Hryvnia ucraineană)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pret_vanzare"
                label="Preț de vânzare"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  className="w-full"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  disabled={!isManualPrice}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Mod preț vânzare">
                <Switch
                  checked={isManualPrice}
                  onChange={setIsManualPrice}
                  checkedChildren="Manual"
                  unCheckedChildren="Automat (+30%)"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button onClick={onCancel}>
            Anulează
          </Button>
          <Button type="primary" htmlType="submit" className="bg-blue-600 hover:bg-blue-700">
            {initialValues ? 'Actualizează' : 'Creează'} Produs
          </Button>
        </div>
      </Form>

      <CategoriesManager
        visible={isCategoriesModalVisible}
        onClose={handleCategoriesModalClose}
      />
    </>
  );
}

export default ProdusForm; 